import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form } from 'antd';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw as FieldContainerIwBase } from '@/_pages/interwill/components/FieldContainerIw';
import { Input } from '@/shared/components';
import { SelectGender } from '@/shared/components/forms/fields/SelectGender';
import { COLORS } from '@/shared/constants/colors';
import { getPersonFullName } from '@/shared/helpers/names';
import { DatePicker } from '@/shared/uikit/DatePicker';
import { Text } from '@/shared/uikit/Text';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { FullNameModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

import { FullNameFormParams } from './types';

export const FullNameModal: FC<FullNameModalProps> = ({ title, beneficiaryData, onSubmit }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'fullname' });

  const onClose = useModalsStore((s) => s.closeModal);

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
    watch,
  } = useForm<FullNameFormParams>({
    defaultValues: {
      firstName: beneficiaryData?.firstName,
      secondName: beneficiaryData?.secondName,
      lastName: beneficiaryData?.lastName,
      birthDate: beneficiaryData?.birthDate,
      gender: beneficiaryData?.gender,
    },
  });

  const firstName = watch('firstName') || '';
  const secondName = watch('secondName') || '';
  const lastName = watch('lastName') || '';

  const onSubmitHandler = async (data: FullNameFormParams) => {
    await onSubmit({
      ...data,
      birthDate:
        typeof data.birthDate === 'object' ? data?.birthDate?.toISOString() : data?.birthDate,
    });
    onClose();
  };

  return (
    <ModalContainerV1 title={title ?? t('title')}>
      <InterwillStyledForm onFinish={formHandleSubmit(onSubmitHandler)}>
        <InnerForm>
          <Controller
            name={'lastName'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
          <Controller
            name={'firstName'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
          <Controller
            name={'secondName'}
            control={control}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
        </InnerForm>
        <PreviewBlock>
          <Text>{t('fullNamePreview')}</Text>
          <Preview>
            {getPersonFullName({ firstName, secondName, lastName }) || t('autofilled')}
          </Preview>
        </PreviewBlock>
        <InnerForm>
          <Controller
            name={'birthDate'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <DatePicker
                      format={'DD MMMM YYYY'}
                      style={{
                        width: '100%',
                        height: 40,
                      }}
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      {...field}
                      value={
                        field.value && typeof field.value === 'object'
                          ? field.value.toISOString()
                          : field.value
                      }
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
          <Controller
            name={'gender'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectGender placeholder={t(`fields.${field.name}.label`)} {...field} />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
        </InnerForm>
        <Buttons>
          <ButtonIw loading={isSubmitting} disabled={isSubmitting} htmlType="submit">
            {t('btn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const Preview = styled(Text)`
  opacity: 0.5;
  color: ${COLORS.systemDark200};
`;

const PreviewBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 16px 0;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;
`;

const FieldContainerIw = styled(FieldContainerIwBase)`
  flex: 0 calc(50% - 8px);
`;
